import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchableBusinessOption {
    id: string;
    name: string;
    subtitle?: string;
}

interface SearchableBusinessSelectProps {
    options: SearchableBusinessOption[];
    value?: string;
    onChange: (id: string) => void;
    placeholder?: string;
    emptyLabel?: string;
    widthClassName?: string;
    searchPlaceholder?: string;
    buttonClassName?: string;
    menuClassName?: string;
    optionClassName?: string;
    showSubtitleWhenClosed?: boolean;
}

const SearchableBusinessSelect: React.FC<SearchableBusinessSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select business',
    emptyLabel = 'No businesses found',
    widthClassName = 'w-64',
    searchPlaceholder = 'Search businesses...',
    buttonClassName = 'relative w-full bg-white border border-gray-300 rounded-md py-3 pl-3 pr-10 focus:ring-indigo-500 focus:border-indigo-500 flex items-center text-left',
    menuClassName = 'absolute top-11 right-0 z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden',
    optionClassName = 'w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-b-0',
    showSubtitleWhenClosed = true,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const selected = options.find(option => option.id === value);

    const filteredOptions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(option =>
            option.name.toLowerCase().includes(q) ||
            option.subtitle?.toLowerCase().includes(q)
        );
    }, [options, query]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    return (
        <div ref={containerRef} className={`relative ${widthClassName}`}>
            <button
                type="button"
                onClick={() => setOpen(prev => !prev)}
                className={buttonClassName}
            >
                <div className="min-w-0 pr-8">
                    <div className="text-sm text-gray-700 truncate">
                        {selected?.name || placeholder}
                    </div>
                    {showSubtitleWhenClosed && selected?.subtitle && (
                        <div className="text-xs text-gray-500 truncate">{selected.subtitle}</div>
                    )}
                </div>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </button>

            {open && (
                <div className={menuClassName}>
                    <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {filteredOptions.map(option => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                    onChange(option.id);
                                    setOpen(false);
                                }}
                                className={optionClassName}
                            >
                                <div className="text-sm font-medium text-gray-900 truncate">{option.name}</div>
                                {option.subtitle && (
                                    <div className="text-xs text-gray-500 truncate">{option.subtitle}</div>
                                )}
                            </button>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-4 py-6 text-sm text-gray-500">{emptyLabel}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableBusinessSelect;
